import { CreateSessionRequest, SessionResponse } from '@sos/shared';

export interface SessionGeneratorModalOptions {
  apiBaseUrl: string;
  agencyId?: string;
  dispatcherName?: string;
  onSessionCreated: (session: SessionResponse) => void;
  onClose: () => void;
}

export class SessionGeneratorModal {
  private element: HTMLElement;

  constructor(private readonly options: SessionGeneratorModalOptions) {
    this.element = document.createElement('div');
    this.element.className = 'modal-backdrop';
    this.render();
  }

  private render(): void {
    const randomFolio = `CAD-911-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;

    this.element.innerHTML = `
      <div class="modal-dialog">
        <div style="display:flex; justify-content:space-between; align-items:center;">
          <div style="display:flex; align-items:center; gap:10px;">
            <div style="background:rgba(60,110,180,0.18); border:1px solid #3c6eb4; border-radius:8px; padding:6px; color:#51a2da;">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
            </div>
            <div>
              <h2 style="font-size:1.15rem; font-weight:800; color:#fff;">Generar Enlace SOS VideoCall™</h2>
              <p style="font-size:0.8rem; color:#94a3b8;">Tele-Triage 911 sin app (&lt;500ms) &bull; Despacho instantáneo vía SMS</p>
            </div>
          </div>
          <button id="btn-modal-close" style="background:none; border:none; color:var(--text-muted); font-size:1.5rem; cursor:pointer;">✕</button>
        </div>

        <form id="create-session-form" style="display:flex; flex-direction:column; gap:14px;">
          <div class="form-group">
            <label class="form-label" for="inp-phone">Número de Teléfono del Reportante</label>
            <div style="display:flex; gap:8px;">
              <select id="sel-country-code" class="form-input" style="width:120px; padding:0 8px; font-family:var(--font-mono); font-size:0.85rem;">
                <option value="+52">🇲🇽 +52 (MX)</option>
                <option value="+1">🇺🇸 +1 (US)</option>
                <option value="">🌐 Otro / Directo</option>
              </select>
              <input
                id="inp-phone"
                class="form-input"
                style="flex:1;"
                type="tel"
                placeholder="Teléfono móvil (10 dígitos o con código)"
                required
                autocomplete="off"
              />
            </div>
          </div>

          <div style="display:grid; grid-template-columns:1fr 1fr; gap:12px;">
            <div class="form-group">
              <label class="form-label" for="inp-cad-folio">Folio CAD / 911 (Opcional)</label>
              <input
                id="inp-cad-folio"
                class="form-input"
                type="text"
                placeholder="ej. CAD-2026-04821"
                autocomplete="off"
              />
            </div>

            <div class="form-group">
              <label class="form-label" for="inp-folio">Folio SOS VideoCall</label>
              <input
                id="inp-folio"
                class="form-input"
                type="text"
                value="${randomFolio}"
                required
              />
            </div>
          </div>

          <div class="form-group">
            <label class="form-label" for="inp-dispatcher-name">Paramédico / Despachador</label>
            <input
              id="inp-dispatcher-name"
              class="form-input"
              type="text"
              value="${this.options.dispatcherName || 'Despachador 911'}"
              required
            />
          </div>

          <div id="modal-error-box" style="display:none; background:rgba(239,68,68,0.15); border:1px solid #ef4444; border-radius:8px; padding:10px; font-size:0.85rem; color:#fca5a5;"></div>

          <button id="btn-submit-session" class="btn-primary" style="height:48px; justify-content:center; margin-top:8px; font-size:0.95rem;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="22 2 15 22 11 13 2 9 22 2"></polyline></svg>
            ENVIAR SMS Y CONECTAR SALA
          </button>
        </form>
      </div>
    `;

    this.element.querySelector('#btn-modal-close')?.addEventListener('click', () => {
      this.close();
    });

    const form = this.element.querySelector('#create-session-form') as HTMLFormElement;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });
  }

  private async handleSubmit(): Promise<void> {
    const phoneInput = this.element.querySelector('#inp-phone') as HTMLInputElement;
    const countrySelect = this.element.querySelector('#sel-country-code') as HTMLSelectElement;
    const folioInput = this.element.querySelector('#inp-folio') as HTMLInputElement;
    const cadInput = this.element.querySelector('#inp-cad-folio') as HTMLInputElement;
    const nameInput = this.element.querySelector('#inp-dispatcher-name') as HTMLInputElement;
    const submitBtn = this.element.querySelector('#btn-submit-session') as HTMLButtonElement;
    const errorBox = this.element.querySelector('#modal-error-box') as HTMLElement;

    const countryCode = countrySelect?.value || '';
    let cleanPhone = phoneInput.value.trim().replace(/[^\d+]/g, '');
    if (countryCode && !cleanPhone.startsWith('+')) {
      cleanPhone = `${countryCode}${cleanPhone}`;
    } else if (!cleanPhone.startsWith('+')) {
      cleanPhone = `+${cleanPhone}`;
    }

    const payload: CreateSessionRequest = {
      phoneNumber: cleanPhone,
      incidentFolio: folioInput.value.trim(),
      cadFolio: cadInput?.value.trim() || undefined,
      dispatcherId: 'disp_operativo',
      dispatcherName: nameInput.value.trim(),
      agencyId: this.options.agencyId || 'ag_main',
      expirationMinutes: 15
    };

    try {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Despachando invitación...';
      errorBox.style.display = 'none';

      const res = await fetch(`${this.options.apiBaseUrl}/api/v1/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        let errDetail = `Error del servidor HTTP ${res.status}`;
        try {
          const errJson = await res.json();
          if (errJson && errJson.message) errDetail = errJson.message;
        } catch (_) {
          if (res.status === 502) {
            errDetail = 'El servicio de señalización/API está detenido o reiniciándose en el servidor (502 Bad Gateway).';
          } else if (res.status === 504) {
            errDetail = 'Tiempo de espera agotado con el backend (504 Gateway Timeout).';
          } else if (res.status === 404) {
            errDetail = 'Ruta de la API no encontrada (404 Not Found).';
          }
        }
        throw new Error(errDetail);
      }

      const json = (await res.json()) as { success: boolean; data?: SessionResponse; message?: string };

      if (!json.success || !json.data) {
        throw new Error(json.message || 'Error al generar la sesión en el servidor');
      }

      this.options.onSessionCreated(json.data);
      this.close();
    } catch (err) {
      submitBtn.disabled = false;
      submitBtn.textContent = 'ENVIAR SMS Y CONECTAR SALA';
      errorBox.textContent = err instanceof Error ? err.message : String(err);
      errorBox.style.display = 'block';
    }
  }

  public close(): void {
    this.element.remove();
    this.options.onClose();
  }

  public mount(container: HTMLElement): void {
    container.appendChild(this.element);
  }
}
